import { getEnv } from '@repo/utils';
import { BordeauxMachineContext, ThirdParty } from '@repo/shared-types';

import type { buildUserSyncIds } from './userIds';

const LI_TREATMENT_RATE = 0.95;
const ATS_ANALYTICS_PID = '12243';
const CRITEO_PUBLISHER_ID = '6783';
const SCHAIN_ASI = 'futureplc.com';
const CONSENT_TIMEOUT_MS = 3000;
const RTD_AUCTION_DELAY_MS = 500;
const USER_SYNC_AUCTION_DELAY_MS = 500;
const USER_SYNC_DELAY_MS = 3000;

export const initialiseLiModule = (): void => {
  if (getEnv().liModuleEnabled === undefined) {
    getEnv().liModuleEnabled = Math.random() < LI_TREATMENT_RATE;
    getEnv().liTreatmentRate = LI_TREATMENT_RATE;
  }
};

export const paapiConfig = { paapi: { enabled: true, defaultForSlots: 1 } } as const;

export const analyticsConfig = [{ provider: 'atsAnalytics', options: { pid: ATS_ANALYTICS_PID } }];

export const buildPrebidConfig = (
  context: Pick<BordeauxMachineContext, 'thirdPartyApiConfig'>,
  userSyncIds: ReturnType<typeof buildUserSyncIds>,
) => {
  const { sellerID } = context.thirdPartyApiConfig[ThirdParty.GLOBAL];

  return {
    useBidCache: true,
    mediaTypePriceGranularity: {
      banner: {
        buckets: [
          { max: 10, increment: 0.01 },
          { max: 20, increment: 0.05 },
          { max: 50, increment: 0.25 },
          { max: 75, increment: 1 },
        ],
      },
      video: {
        buckets: [{ precision: 2, max: 70, increment: 0.1 }],
      },
      native: {
        buckets: [
          { min: 0.05, max: 3.95, increment: 0.05 },
          { min: 4, max: 9.75, increment: 0.25 },
          { min: 10, max: 30, increment: 1 },
        ],
      },
    },
    consentManagement: {
      usp: { cmpApi: 'iab', timeout: CONSENT_TIMEOUT_MS },
      gdpr: {
        cmpApi: 'iab',
        defaultGdprScope: true,
        timeout: CONSENT_TIMEOUT_MS,
        rules: [
          {
            purpose: 'storage',
            enforcePurpose: true,
            enforceVendor: true,
            vendorExceptions: ['permutive', 'pubxai'],
          },
          {
            purpose: 'basicAds',
            enforcePurpose: true,
            enforceVendor: true,
            vendorExceptions: [],
          },
        ],
      },
      gpp: { cmpApi: 'iab', timeout: CONSENT_TIMEOUT_MS },
    },
    realTimeData: {
      auctionDelay: RTD_AUCTION_DELAY_MS,
      dataProviders: [
        {
          name: 'permutive',
          waitForIt: true,
          params: { acBidders: ['ix', 'appnexus', 'rubicon', 'pubmatic', 'ttd'] },
        },
      ],
    },
    userSync: {
      auctionDelay: USER_SYNC_AUCTION_DELAY_MS,
      syncEnabled: true,
      syncsPerBidder: 5,
      syncDelay: USER_SYNC_DELAY_MS,
      filterSettings: {
        image: { bidders: '*', filter: 'include' },
        iframe: { bidders: '*', filter: 'include' },
      },
      userIds: userSyncIds,
    },
    ...(sellerID && {
      schain: {
        validation: 'strict',
        config: {
          complete: 1,
          ver: '1.0',
          nodes: [{ asi: SCHAIN_ASI, sid: sellerID, hp: 1 }],
        },
      },
    }),
  };
};

export const criteoConfig = {
  bidders: ['criteo'],
  config: { ortb2: { site: { publisher: { id: CRITEO_PUBLISHER_ID } } } },
} as const;

export const bidderSettings = {
  standard: { storageAllowed: true },
} as const;
